import { updateCollectedTestData } from "@store/tests/testSlice";
import { EyeType, Snapshot } from "types/testTypes";
import { isVersionEnoughForHeadsetSceneView } from "@utils/VersionUtils";
import { store } from "@store/index";

// Enum for WebRTC message types - extend as needed
export enum WebRTCMessageType {
  EYE_IMAGE = "eye_image",
  SCENE_IMAGE = "scene_image",
  UNKNOWN = "unknown",
}

type SceneImageListener = (imageUrl: string | null) => void;
const sceneImageListeners = new Set<SceneImageListener>();
let latestSceneImageUrl: string | null = null;

export function subscribeSceneImage(listener: SceneImageListener): () => void {
  sceneImageListeners.add(listener);
  listener(latestSceneImageUrl);
  return () => {
    sceneImageListeners.delete(listener);
  };
}

function emitSceneImage(imageUrl: string | null): void {
  latestSceneImageUrl = imageUrl;
  sceneImageListeners.forEach((listener) => listener(imageUrl));
}

export function clearSceneImage(): void {
  emitSceneImage(null);
}

// Define the structure for WebRTC message handlers
type WebRTCMessageHandler = (message: any) => void;

// This map will hold all the registered handlers for WebRTC messages
export const webRTCMessageHandlers: Map<WebRTCMessageType, WebRTCMessageHandler> = new Map<
  WebRTCMessageType,
  WebRTCMessageHandler
>([
  [
    WebRTCMessageType.EYE_IMAGE,
    (message) => {
      try {
        let eyeData = message.parsedData;
        const data = message.data;

        if (!eyeData && typeof data === "string") {
          try {
            eyeData = JSON.parse(data);
          } catch (e) {
            console.error("Failed to parse eye image data string:", e);
            return;
          }
        } else if (!eyeData && data instanceof ArrayBuffer) {
          try {
            const decoder = new TextDecoder("utf-8");
            const jsonString = decoder.decode(data);
            eyeData = JSON.parse(jsonString);
          } catch (e) {
            console.error("Failed to parse eye image data from binary:", e);
            return;
          }
        } else if (!eyeData) {
          console.error("Unsupported data type for eye image");
          return;
        }

        if (!eyeData) {
          console.error("No valid eye data found in message");
          return;
        }

        const { frameCount, timestamp, width, height, imageFormat, data: base64Data } = eyeData;

        if (!base64Data) {
          console.error("No image data in eye message");
          return;
        }

        const imageUrl = `data:image/${imageFormat || "jpeg"};base64,${base64Data}`;

        const snapshot: Snapshot = {
          eyeImageData: {
            eyeType: eyeData.eyeIndex === 0 ? EyeType.LeftEye : EyeType.RightEye,
            frameCount: frameCount || 0,
            timestamp: timestamp || Date.now(),
            width: width || 0,
            height: height || 0,
            imageFormat: imageFormat || "jpeg",
            imageUrl,
            base64Data,
          },
        };

        store.dispatch(updateCollectedTestData(snapshot));

        console.log("Processed eye image data", { frameCount, timestamp });
      } catch (error) {
        console.error("Error processing eye image data:", error);
      }
    },
  ],
  [
    WebRTCMessageType.SCENE_IMAGE,
    (message) => {
      try {
        const parsed = message.parsedData;
        if (!parsed || parsed.kind !== "scene") return;
        if (typeof parsed.frame !== "string" || parsed.frame.length === 0) return;
        const format = typeof parsed.format === "string" ? parsed.format.toLowerCase() : "jpeg";
        if (format !== "jpeg" && format !== "jpg") return;
        emitSceneImage(`data:image/jpeg;base64,${parsed.frame}`);
      } catch (error) {
        console.error("Error processing scene image data:", error);
      }
    },
  ],
  [
    WebRTCMessageType.UNKNOWN,
    (message) => {
      console.log("Received unknown WebRTC message type:", message);
    },
  ],
]);

export function getWebRTCMessageType(message: any): WebRTCMessageType {
  try {
    const data = message.data;

    let parsedData: any = null;
    if (typeof data === "string") {
      try {
        parsedData = JSON.parse(data);
      } catch (e) {
        console.error("Error determining WebRTC message type:", e);
      }
    } else if (data instanceof ArrayBuffer) {
      try {
        parsedData = JSON.parse(new TextDecoder("utf-8").decode(data));
      } catch (e) {
        return WebRTCMessageType.EYE_IMAGE;
      }
    }

    if (parsedData) {
      message.parsedData = parsedData;

      if (parsedData.kind === "scene") {
        return isVersionEnoughForHeadsetSceneView(store.getState().product.currentVersion)
          ? WebRTCMessageType.SCENE_IMAGE
          : WebRTCMessageType.UNKNOWN;
      }

      if (parsedData.imageFormat && parsedData.data) {
        return WebRTCMessageType.EYE_IMAGE;
      }

      if (data instanceof ArrayBuffer) {
        return WebRTCMessageType.EYE_IMAGE;
      }
    }

    // Default case
    return WebRTCMessageType.UNKNOWN;
  } catch (error) {
    console.error("Error determining WebRTC message type:", error);
    return WebRTCMessageType.UNKNOWN;
  }
}

export function processWebRTCMessage(message: any): void {
  const messageType = getWebRTCMessageType(message);

  if (webRTCMessageHandlers.has(messageType)) {
    webRTCMessageHandlers.get(messageType)(message);
  } else {
    console.warn(`No handler registered for WebRTC message type: ${messageType}`);
    webRTCMessageHandlers.get(WebRTCMessageType.UNKNOWN)(message);
  }
}

export function extractImageDataFromBinary(data: ArrayBuffer): any {
  try {
    const decoder = new TextDecoder("utf-8");
    const jsonString = decoder.decode(data);

    return JSON.parse(jsonString);
  } catch (e) {
    console.error("Error extracting image data from binary:", e);
    return null;
  }
}
